import { isNotDefined } from '@sgme/fp';

import type { MarginConverter } from '@/App/tiles/shared/CommercialMargin/MarginSection.tsx';
import { bidAskPairToString, parseBidAskPair } from '@/utils/bidAskPairEdition.ts';
import {
  type BidAskPoint,
  type BidAskPPS,
  convertBidAskPointToBidAskPPS,
  convertBidAskPPSToBidAskPoints,
} from '@/utils/number-point.ts';

/**
 * Converter for Spot margins (Cash)
 * Conversion: points / 10 for PPS display
 */
export const createSpotMarginConverter = (): MarginConverter => ({
  toDisplay: (value) => ({
    bid: value.bid / 10,
    ask: value.ask / 10,
  }),

  fromDisplay: (value) => ({
    bid: value.bid * 10,
    ask: value.ask * 10,
  }),

  toString: (value) => `${value.bid.toFixed(1)}/${value.ask.toFixed(1)}`,

  fromString: (text) => {
    const [bid, ask] = text.split('/').map((v) => Number.parseFloat(v));

    if (isNotDefined(bid) || Number.isNaN(bid) || isNotDefined(ask) || Number.isNaN(ask)) {
      return undefined;
    }

    return { bid, ask };
  },
});

/**
 * Converter for Forward margins
 * No conversion needed (direct values)
 */
export const createForwardMarginConverter = (): MarginConverter => ({
  toDisplay: (value) => value,

  fromDisplay: (value) => value,

  toString: (value) => `${value.bid}/${value.ask}`,

  fromString: (text) => {
    const [bid, ask] = text.split('/').map((v) => Number.parseFloat(v));

    if (isNotDefined(bid) || Number.isNaN(bid) || isNotDefined(ask) || Number.isNaN(ask)) {
      return undefined;
    }

    return { bid, ask };
  },
});

/**
 * Converter for Swap margins
 * Uses existing points <-> PPS conversion functions
 */
export const createSwapMarginConverter = (): MarginConverter => ({
  toDisplay: (value: BidAskPoint): BidAskPPS => convertBidAskPointToBidAskPPS(value),

  fromDisplay: (value: BidAskPPS): BidAskPoint => convertBidAskPPSToBidAskPoints(value),

  toString: (value) => bidAskPairToString(value),

  fromString: (text) => {
    const parsed = parseBidAskPair(text, { numbersFlavor: 'float', allowNegative: true });

    return parsed ?? undefined;
  },
});
